"use client";

/**
 * The trails of a programme at a glance, so how far and how hard the walking
 * is can be seen without reading the programme through: a box per trail
 * walked, in the order it is walked, with its distance, its difficulty as the
 * bar alone, and the day it is on ("Sexta, dia 8 (out)" -- on a one-day event
 * too, where it is the event's date; without a start date, "Dia 2" only when
 * there is more than one day). Each box opens the programme at that trail.
 * One row, snap-scrolled sideways, fading out on the right while there is more
 * -- or, for a programme walking one trail, a single box the width of the page,
 * with room for the bar to say Fácil and Difícil at its ends.
 * Nothing for a programme walking no trail with a distance or a difficulty.
 */
import { useEffect, useRef, useState } from "react";
import { HikingIcon } from "@/components/ui/HikingIcon";
import { addDays } from "@/lib/eventTime";
import { dayLabel } from "@/lib/format";
import type { EventSchedule, ScheduleEntry } from "@/lib/types/event";
import { DifficultyBar } from "@/components/event/DifficultyBar";
import { isDifficulty, trailTitle } from "@/components/event/labels";
import { scheduleKm } from "@/components/event/schedule";

/** the trails the summary shows: those with a distance or a grade */
export const summaryTrails = (schedule: EventSchedule | null | undefined) =>
  ((schedule && schedule.entries) || []).filter((entry) => entry.trail && (Number(entry.trail.length) > 0 || isDifficulty(entry.trail.difficulty)));

/** "Sexta, dia 8 (out)"; "Dia 2" when the first day's date is not known */
export const shortDayLabel = (startDate: string | null | undefined, day: number) => (startDate ? dayLabel(addDays(startDate, day - 1)) : `Dia ${day}`);

export function TrailBox({ entry, day, wide = false, onClick, className = "" }: { entry: ScheduleEntry; day?: string | null; wide?: boolean; onClick?: () => void; className?: string }) {
  const trail = entry.trail!;
  const length = (
    <span className="inline-flex items-center gap-1.5 text-[15px] font-extrabold text-ink">
      <HikingIcon size={15} className="text-brand" />
      {Number(trail.length) > 0 ? scheduleKm(Number(trail.length)) : "Trilho"}
    </span>
  );
  const dayLine = day ? <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-mute">{day}</span> : null;
  const box = "pressable rounded-[12px] border border-line bg-surface px-3 py-2.5 text-left shadow-[0_1px_2px_rgba(20,30,20,0.05)] transition-colors hover:border-brand-light";
  if (wide) {
    return (
      <button type="button" title={trailTitle(trail)} onClick={onClick} className={`${box} flex w-full items-center justify-between gap-3 ${className}`}>
        <span className="flex min-w-0 flex-col items-start gap-1.5">
          {dayLine}
          {length}
        </span>
        <DifficultyBar difficulty={trail.difficulty} compact words size={14} className="shrink-0" />
      </button>
    );
  }
  return (
    <button
      type="button"
      title={trailTitle(trail)}
      onClick={onClick}
      className={`${box} flex min-w-[112px] shrink-0 snap-start flex-col items-start gap-1.5 ${className}`}
    >
      {dayLine}
      {length}
      <DifficultyBar difficulty={trail.difficulty} compact size={10} />
    </button>
  );
}

export function TrailsSummary({ schedule, onOpen, className = "" }: { schedule: EventSchedule | null | undefined; onOpen?: (entry: ScheduleEntry) => void; className?: string }) {
  const row = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  const trails = summaryTrails(schedule);
  const count = trails.length;

  // faded on the right only while there is more of it to scroll to
  useEffect(() => {
    const el = row.current;
    if (!el) return;
    const mark = () => setMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
    const frame = requestAnimationFrame(mark);
    el.addEventListener("scroll", mark, { passive: true });
    window.addEventListener("resize", mark);
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener("scroll", mark);
      window.removeEventListener("resize", mark);
    };
  }, [count]);

  if (!schedule || count === 0) {
    return null;
  }
  const entries = schedule.entries || [];
  const showDay = !!schedule.start_date || new Set(entries.map((entry) => entry.day)).size > 1;
  const dayOf = (entry: ScheduleEntry) => (showDay ? shortDayLabel(schedule.start_date, entry.day) : null);

  if (count === 1) {
    return <TrailBox entry={trails[0]} day={dayOf(trails[0])} wide onClick={() => onOpen?.(trails[0])} className={className} />;
  }

  return (
    <div className={`relative ${className}`}>
      <div ref={row} className="scroll-x flex snap-x gap-2 pb-1">
        {trails.map((entry) => (
          <TrailBox key={entry.id} entry={entry} day={dayOf(entry)} onClick={() => onOpen?.(entry)} />
        ))}
      </div>
      <div
        className={`pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-[var(--fade-to,var(--surface))] to-transparent transition-opacity duration-200 ${more ? "opacity-100" : "opacity-0"}`}
      />
    </div>
  );
}
